import React from 'react';
import { Template } from '../constants/templates';

interface TemplatePreviewProps {
  template: Template;
  className?: string;
}

export const TemplatePreview: React.FC<TemplatePreviewProps> = ({ template, className = '' }) => {
  // エラーハンドリング
  if (!template) {
    console.error('TemplatePreview: template is null or undefined');
    return <div className={`aspect-square rounded-lg bg-gray-200 ${className}`}>テンプレートがありません</div>;
  }
  
  // デバッグ: テンプレート情報を出力
  console.log('=== TemplatePreview Debug ===');
  console.log('Template ID:', template.id);
  console.log('Template Name:', template.name);
  console.log('Has backgroundImage?', !!template.backgroundImage);
  console.log('backgroundImage value:', template.backgroundImage);
  console.log('Full template object:', template);
  // クリエイティブセクションの背景画像風プレビュー
  const getCreativeBackground = (templateId: string) => {
    const backgroundMap: { [key: string]: { image: string; icon: string; overlay: string } } = {
      'white_wood': {
        image: '/images/templates/white-wood.jpg',
        icon: '🌳',
        overlay: 'radial-gradient(circle at 20% 30%, rgba(139, 69, 19, 0.1) 0%, transparent 50%)'
      },
      'salon_interior': {
        image: '/images/templates/salon-interior.jpg',
        icon: '💇‍♀️',
        overlay: 'radial-gradient(circle at 70% 20%, rgba(255, 192, 203, 0.2) 0%, transparent 50%)'
      },
      'cafe_cozy': {
        image: '/images/templates/cafe-cozy.jpg',
        icon: '☕',
        overlay: 'radial-gradient(circle at 30% 70%, rgba(255, 248, 220, 0.3) 0%, transparent 50%)'
      },
      'concert_hall': {
        image: '/images/templates/concert-hall.jpg',
        icon: '🎵',
        overlay: 'radial-gradient(circle at 50% 20%, rgba(255, 215, 0, 0.2) 0%, transparent 50%)'
      },
      'beach_sunset': {
        image: '/images/templates/beach-sunset.jpg',
        icon: '🌅',
        overlay: 'radial-gradient(circle at 80% 30%, rgba(255, 255, 255, 0.2) 0%, transparent 50%)'
      },
      'urban_street': {
        image: '/images/templates/urban-street.jpg',
        icon: '🏙️',
        overlay: 'radial-gradient(circle at 20% 80%, rgba(52, 152, 219, 0.2) 0%, transparent 50%)'
      }
    };
    
    return backgroundMap[templateId] || {
      image: null,
      icon: '🎨',
      overlay: 'radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)'
    };
  };

  // ビジネスセクションのカラーパレット
  const getBusinessColorPalette = (templateId: string) => {
    const colorMap: { [key: string]: { primary: string; secondary: string; accent: string } } = {
      'studio_white': { primary: '#ffffff', secondary: '#f8fafc', accent: '#e2e8f0' },
      'studio_gray': { primary: '#6b7280', secondary: '#9ca3af', accent: '#d1d5db' },
      'studio_light_purple': { primary: '#a855f7', secondary: '#c084fc', accent: '#e9d5ff' },
      'studio_light_pink': { primary: '#ec4899', secondary: '#f472b6', accent: '#fce7f3' },
      'studio_light_blue': { primary: '#3b82f6', secondary: '#60a5fa', accent: '#dbeafe' },
      'linkedin_style': { primary: '#0077b5', secondary: '#00a0dc', accent: '#e1f5fe' },
      'facebook_style': { primary: '#1877f2', secondary: '#42a5f5', accent: '#e3f2fd' },
      'custom_template': { primary: '#6366f1', secondary: '#8b5cf6', accent: '#e0e7ff' },
      'custom_image': { primary: '#f97316', secondary: '#fb923c', accent: '#fed7aa' }
    };
    
    return colorMap[templateId] || { primary: '#3b82f6', secondary: '#1d4ed8', accent: '#1e40af' };
  };

  // 画像ファイルを使用するテンプレートかどうかを判定
  const hasBackgroundImage = template.backgroundImage;

  if (hasBackgroundImage) {
    // template.backgroundImage を使用
    const finalImage = template.backgroundImage ? `/images/templates/${template.backgroundImage}.jpg` : null;
    
    // アイコンとオーバーレイは固定値を使用
    const icon = '🎨';
    const overlay = 'radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)';
    
    console.log('Template with background image:', template.id, 'Image:', finalImage);
    
    return (
      <div className={`aspect-square rounded-lg overflow-hidden relative ${className}`}>
        {finalImage ? (
          // 実際の画像を表示
          <div className="w-full h-full relative">
            <img 
              src={finalImage} 
              alt={template.name}
              className="w-full h-full object-cover"
              onLoad={() => console.log('Image loaded successfully:', finalImage)}
              onError={(e) => {
                console.log('Image load error:', finalImage, e);
                console.log('Current URL:', window.location.href);
                console.log('Trying to load:', finalImage);
              }}
            />
            {/* オーバーレイ効果 */}
            <div 
              className="absolute inset-0"
              style={{ background: overlay }}
            />
            
            {/* 画像がある場合はアイコンを非表示 */}
            
            {/* コーナーの装飾 */}
            <div className="absolute top-1 right-1 w-3 h-3 bg-white bg-opacity-30 rounded-full"></div>
            <div className="absolute bottom-1 left-1 w-2 h-2 bg-white bg-opacity-20 rounded-full"></div>
          </div>
        ) : (
          // グラデーション表示（画像がない場合）
          <div 
            className="w-full h-full"
            style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 50%, #1e40af 100%)' }}
          >
            {/* オーバーレイ効果 */}
            <div 
              className="absolute inset-0"
              style={{ background: overlay }}
            />
            
            {/* 中央のアイコンエリア */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center backdrop-blur-sm">
                <span className="text-lg">{icon}</span>
              </div>
            </div>
            
            {/* コーナーの装飾 */}
            <div className="absolute top-1 right-1 w-3 h-3 bg-white bg-opacity-30 rounded-full"></div>
            <div className="absolute bottom-1 left-1 w-2 h-2 bg-white bg-opacity-20 rounded-full"></div>
          </div>
        )}
      </div>
    );
  }

  // ビジネスセクション用のカラープレビュー（backgroundImageがない場合のみ）
  const { primary, secondary, accent } = getBusinessColorPalette(template.id);
  
  console.log('Business template without background image:', template.id, 'Colors:', { primary, secondary, accent });

  return (
    <div className={`aspect-square rounded-lg overflow-hidden relative ${className}`}>
      <div 
        className="w-full h-full"
        style={{
          background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 50%, ${accent} 100%)`
        }}
      >
        {/* テクスチャ効果 */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            background: `radial-gradient(circle at 30% 20%, ${primary} 0%, transparent 50%), 
                        radial-gradient(circle at 70% 80%, ${secondary} 0%, transparent 50%)`
          }}
        />
        
        {/* 中央のアイコンエリア */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center backdrop-blur-sm">
            <div className="w-5 h-5 bg-white bg-opacity-40 rounded-full"></div>
          </div>
        </div>
        
        {/* コーナーの装飾 */}
        <div className="absolute top-1 right-1 w-3 h-3 bg-white bg-opacity-30 rounded-full"></div>
        <div className="absolute bottom-1 left-1 w-2 h-2 bg-white bg-opacity-20 rounded-full"></div>
      </div>
    </div>
  );
};
